import { DurationString, getDurationStringSeconds } from '@chronosphereio/core';
import { PromQLEditor } from '@chronosphereio/editors/prometheus';
import { EventPayload, PrometheusEventQuerySpec, parseTemplateVariableNames, replaceTemplateVariables } from '@/core';
import { EventQueryPlugin, renameSeriesWithLabels } from '../../../plugin-system';
import {
  DEFAULT_PROM_URL,
  getPrometheusClient,
  getPrometheusTimeRange,
  getRangeStep,
  PrometheusClient,
} from '../../model';
import { appMetadataToHTTPHeaders } from '../../utils';
import { injectGlobalFilters, interpolateVariables } from '../prometheus-time-series-query/variables';
import { groupEvents } from './utils';

// TODO: Make this better
const DEFAULT_ANNOTATION_TITLE = 'Event';

/**
 * The core Prometheus EventQuery plugin for Perses.
 */
export const PrometheusEventQuery: EventQueryPlugin<PrometheusEventQuerySpec> = {
  getEvents: async (spec, context) => {
    const client: PrometheusClient = getPrometheusClient(context.config);
    const headers = context.appMetadata ? appMetadataToHTTPHeaders(context.appMetadata) : {};
    const { query } = spec;

    const { start, end } = getPrometheusTimeRange(context.timeRange);
    const minStep = getDurationStringSeconds(spec.min_step as DurationString);
    const specStep = getDurationStringSeconds(spec.step as DurationString);
    const timeRange = getPrometheusTimeRange(context.timeRange);
    // Use predefined step if defined in the query.
    const step =
      specStep ||
      getRangeStep({
        timeRange,
        minStepSeconds: minStep,
        suggestedStepMs: context.suggestedStepMs,
      });

    // Make the request to Prom
    const response = await client.rangeQuery(
      {
        query,
        start,
        end,
        step,
      },
      headers
    );

    const events: EventPayload[] = [];

    // If there is a datapoint in the range, add it to the events
    // TODO: Make this better with endTimestamp if there are sequential datapoints
    for (const result of response.data?.result ?? []) {
      let title = renameSeriesWithLabels(spec.title_format ?? DEFAULT_ANNOTATION_TITLE, result.metric);
      title = replaceTemplateVariables(title, context.variableState);

      result.values?.forEach((value) => {
        const val = parseFloat(value[1]);
        // Only add the event if the value is >= 1 (consistent with Grafana)
        // Context: https://github.com/chronosphereio/monorepo/blob/ea6b540de84e87a4186d29b7ec31ca6ad067cb36/ui/grafana/src/chronosphere-prometheus/model/annotations-query.ts#L85
        if (val >= 1) {
          events.push({
            icon: 'diamond',
            title: title,
            timestampMs: value[0] * 1000,
          });
        }
      });
    }
    const stepMs = step * 1000;

    return {
      events: groupEvents(events, stepMs),
    };
  },
  OptionsEditorComponent: (props) => {
    return (
      <PromQLEditor
        queryApiEndpoint={DEFAULT_PROM_URL}
        value={props.value.query}
        onChange={(q) => props.onChange({ ...props.value, query: q })}
      />
    );
  },
  createInitialOptions: () => ({
    query: '',
  }),
  dependsOn: (spec) => {
    return {
      variables: parseTemplateVariableNames(spec.query),
    };
  },
  interpolateVariables,
  injectGlobalFilters,
};
